import { useEffect, useRef, useState } from 'react';
import { P, LIMITS } from '@tasca/shared';
import { ApiError, rest, useStore } from '@tasca/client-sdk';
import type { Guild, Member, Role } from '@tasca/client-sdk';
import { hueOf, initials } from '../lib/avatar.js';
import { has } from '../lib/permissions.js';
import { uploadUrl } from '../lib/media.js';
import { Icon } from './Icon.js';
import { confirm, notify } from './Overlays.js';

/**
 * Definições do servidor: identidade e permissões.
 *
 * O servidor já sabia fazer tudo isto — mudar o nome, trocar o ícone, criar
 * grupos de permissões, dá-los a alguém — e nada disso tinha por onde se
 * fazer. Era a maior distância entre o que a API faz e o que a app deixa
 * fazer.
 *
 * Duas secções porque são duas permissões diferentes: MANAGE_GUILD mexe na
 * identidade, MANAGE_ROLES mexe em quem pode o quê. Quem só tem uma vê só a
 * sua.
 *
 * No código isto chama-se `role`, que é o nome da coisa na API; no ecrã
 * nunca aparece a palavra "papel" — quem usa a app pensa nisto como
 * permissões, e dois nomes para a mesma coisa só confundem.
 */

/**
 * O catálogo de permissões, por grupos.
 *
 * Em português e pela ordem em que se pensa nelas, não pela ordem dos bits:
 * quem abre isto quer saber "esta gente pode falar na voz?", não o que é o
 * bit 15.
 */
const GROUPS: { title: string; items: { bit: bigint; label: string }[] }[] = [
  {
    title: 'Geral',
    items: [
      { bit: P.VIEW_CHANNEL, label: 'Ver canais' },
      { bit: P.CREATE_INVITE, label: 'Criar convites' },
      { bit: P.CHANGE_NICKNAME, label: 'Mudar a própria alcunha' },
      { bit: P.MANAGE_NICKNAMES, label: 'Mudar a alcunha dos outros' },
    ],
  },
  {
    title: 'Conversa',
    items: [
      { bit: P.SEND_MESSAGES, label: 'Enviar mensagens' },
      { bit: P.READ_MESSAGE_HISTORY, label: 'Ler o histórico' },
      { bit: P.ATTACH_FILES, label: 'Anexar ficheiros' },
      { bit: P.EMBED_LINKS, label: 'Links com pré-visualização' },
      { bit: P.ADD_REACTIONS, label: 'Reagir' },
      { bit: P.MENTION_EVERYONE, label: 'Mencionar toda a gente' },
      { bit: P.MANAGE_MESSAGES, label: 'Apagar mensagens de outros' },
    ],
  },
  {
    title: 'Voz',
    items: [
      { bit: P.CONNECT, label: 'Entrar em canais de voz' },
      { bit: P.SPEAK, label: 'Falar' },
      { bit: P.VIDEO, label: 'Ligar a câmara' },
      { bit: P.STREAM, label: 'Partilhar o ecrã' },
      { bit: P.USE_VAD, label: 'Falar sem premir para falar' },
      { bit: P.PRIORITY_SPEAKER, label: 'Voz prioritária' },
      { bit: P.MUTE_MEMBERS, label: 'Silenciar outros' },
      { bit: P.DEAFEN_MEMBERS, label: 'Ensurdecer outros' },
      { bit: P.MOVE_MEMBERS, label: 'Mover e desligar outros' },
    ],
  },
  {
    title: 'Moderação',
    items: [
      { bit: P.KICK_MEMBERS, label: 'Expulsar' },
      { bit: P.BAN_MEMBERS, label: 'Banir' },
      { bit: P.MODERATE_MEMBERS, label: 'Pôr de castigo' },
      { bit: P.VIEW_AUDIT_LOG, label: 'Ver o registo de alterações' },
    ],
  },
  {
    title: 'Servidor',
    items: [
      { bit: P.MANAGE_CHANNELS, label: 'Gerir canais' },
      { bit: P.MANAGE_ROLES, label: 'Gerir permissões' },
      { bit: P.MANAGE_GUILD, label: 'Gerir o servidor' },
      { bit: P.MANAGE_EMOJIS, label: 'Gerir emojis' },
      { bit: P.MANAGE_WEBHOOKS, label: 'Gerir webhooks' },
      { bit: P.ADMINISTRATOR, label: 'Administrador — tudo, sem excepção' },
    ],
  },
];

function colorHex(color: number): string {
  return `#${color.toString(16).padStart(6, '0')}`;
}

export function GuildSettings({
  guild,
  onClose,
}: {
  guild: Guild;
  onClose: () => void;
}): React.ReactElement {
  const dialog = useRef<HTMLDialogElement>(null);
  const roles = useStore((s) => s.roles.get(guild.id) ?? []);
  const members = useStore((s) => s.members.get(guild.id) ?? []);

  const canManageGuild = has(guild.permissions, P.MANAGE_GUILD);
  const canManageRoles = has(guild.permissions, P.MANAGE_ROLES);

  const [tab, setTab] = useState<'identidade' | 'permissoes'>(canManageGuild ? 'identidade' : 'permissoes');

  useEffect(() => {
    dialog.current?.showModal();
  }, []);

  return (
    <dialog
      ref={dialog}
      className="settings"
      aria-label={`Definições de ${guild.name}`}
      onCancel={onClose}
      onClick={(e) => {
        if (e.target === dialog.current) onClose();
      }}
    >
      <header className="settings-head">
        <strong>{guild.name}</strong>
        <nav className="settings-tabs">
          {canManageGuild && (
            <button className={tab === 'identidade' ? 'on' : ''} onClick={() => setTab('identidade')}>
              Identidade
            </button>
          )}
          {canManageRoles && (
            <button className={tab === 'permissoes' ? 'on' : ''} onClick={() => setTab('permissoes')}>
              Permissões
            </button>
          )}
        </nav>
        <div className="spacer" />
        <button className="icon" title="Fechar" onClick={onClose}>
          <Icon name="close" />
        </button>
      </header>

      <div className="settings-body">
        {tab === 'identidade' && canManageGuild && <Identity guild={guild} />}
        {tab === 'permissoes' && canManageRoles && (
          <Roles guild={guild} roles={roles} members={members} />
        )}
      </div>
    </dialog>
  );
}

// ─── Identidade ──────────────────────────────────────────────────────────────

function Identity({ guild }: { guild: Guild }): React.ReactElement {
  const [name, setName] = useState(guild.name);
  const [busy, setBusy] = useState(false);

  async function saveName(): Promise<void> {
    const wanted = name.trim();
    if (wanted.length < LIMITS.guildNameMin || wanted === guild.name) return;
    setBusy(true);
    try {
      await rest.updateGuild(guild.id, { name: wanted });
      notify('Nome guardado.');
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Falhou guardar o nome', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="settings-pane">
      <label className="field">
        Nome do servidor
        <div className="field-row">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={LIMITS.guildNameMax}
          />
          <button
            className="primary"
            disabled={busy || name.trim() === guild.name || name.trim().length < LIMITS.guildNameMin}
            onClick={() => void saveName()}
          >
            Guardar
          </button>
        </div>
      </label>

      <ImageField
        guild={guild}
        label="Ícone"
        hint="Quadrado, e pequeno — é isto que aparece no separador do servidor."
        current={guild.iconKey}
        shape="icon"
        upload={(file) => rest.uploadGuildIcon(guild.id, file)}
        clear={() => rest.clearGuildIcon(guild.id)}
      />

      <ImageField
        guild={guild}
        label="Banner"
        hint="Deitado. Aparece por cima da lista de canais."
        current={guild.bannerKey}
        shape="banner"
        upload={(file) => rest.uploadGuildBanner(guild.id, file)}
        clear={() => rest.clearGuildBanner(guild.id)}
      />
    </div>
  );
}

function ImageField({
  guild,
  label,
  hint,
  current,
  shape,
  upload,
  clear,
}: {
  guild: Guild;
  label: string;
  hint: string;
  current: string | null;
  shape: 'icon' | 'banner';
  upload: (file: File) => Promise<Guild>;
  clear: () => Promise<Guild>;
}): React.ReactElement {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  async function pick(file: File | undefined): Promise<void> {
    if (!file) return;
    setBusy(true);
    try {
      await upload(file);
      notify(`${label} actualizado.`);
    } catch (err) {
      notify(err instanceof ApiError ? err.message : `Falhou carregar o ${label}`, 'error');
    } finally {
      setBusy(false);
      // Sem isto, escolher o MESMO ficheiro outra vez não dispara `change`.
      if (input.current) input.current.value = '';
    }
  }

  return (
    <div className="field">
      <span>{label}</span>
      <div className="field-row">
        <div className={`image-preview ${shape}`}>
          {current ? (
            <img src={uploadUrl(current)} alt="" />
          ) : shape === 'icon' ? (
            <span className="image-initials" style={{ ['--hue' as string]: hueOf(guild.id) }}>
              {initials(guild.name)}
            </span>
          ) : (
            <span className="image-empty">sem banner</span>
          )}
        </div>
        <div className="field-actions">
          <button disabled={busy} onClick={() => input.current?.click()}>
            {current ? 'Trocar' : 'Carregar'}
          </button>
          {current && (
            <button
              className="link"
              disabled={busy}
              onClick={() => {
                void clear().catch(() => notify(`Falhou remover o ${label}`, 'error'));
              }}
            >
              Remover
            </button>
          )}
          <small>{hint}</small>
        </div>
      </div>
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        hidden
        onChange={(e) => void pick(e.target.files?.[0])}
      />
    </div>
  );
}

// ─── Permissões ──────────────────────────────────────────────────────────────

function Roles({
  guild,
  roles,
  members,
}: {
  guild: Guild;
  roles: Role[];
  members: Member[];
}): React.ReactElement {
  const [selectedId, setSelectedId] = useState<string | null>(roles[0]?.id ?? null);
  const selected = roles.find((r) => r.id === selectedId) ?? roles[0] ?? null;

  async function create(): Promise<void> {
    try {
      const role = await rest.createRole(guild.id, { name: 'novo grupo' });
      setSelectedId(role.id);
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Falhou criar o grupo', 'error');
    }
  }

  return (
    <div className="roles">
      <div className="roles-list">
        {roles.map((r) => (
          <button
            key={r.id}
            className={`role-row ${r.id === selected?.id ? 'on' : ''}`}
            onClick={() => setSelectedId(r.id)}
          >
            <span
              className="role-dot"
              style={{ background: r.color === 0 ? 'var(--faint)' : colorHex(r.color) }}
            />
            <span className="role-name">{r.name}</span>
          </button>
        ))}
        <button className="role-row add" onClick={() => void create()}>
          <Icon name="plus" />
          novo grupo
        </button>
      </div>

      {selected ? (
        <RoleEditor key={selected.id} guild={guild} role={selected} members={members} />
      ) : (
        <p className="hint">Ainda não há grupos de permissões.</p>
      )}
    </div>
  );
}

function RoleEditor({
  guild,
  role,
  members,
}: {
  guild: Guild;
  role: Role;
  members: Member[];
}): React.ReactElement {
  // O @everyone é o grupo com o id do próprio guild. Não se apaga nem se
  // renomeia — é o chão sobre o qual todos os outros assentam.
  const isEveryone = role.id === guild.id;

  const [name, setName] = useState(role.name);
  const [color, setColor] = useState(role.color);
  const [hoist, setHoist] = useState(role.hoist);
  const [mentionable, setMentionable] = useState(role.mentionable);
  const [permissions, setPermissions] = useState(() => BigInt(role.permissions));
  const [busy, setBusy] = useState(false);

  const dirty =
    name !== role.name ||
    color !== role.color ||
    hoist !== role.hoist ||
    mentionable !== role.mentionable ||
    permissions !== BigInt(role.permissions);

  async function save(): Promise<void> {
    setBusy(true);
    try {
      await rest.updateRole(guild.id, role.id, {
        ...(isEveryone ? {} : { name, color, hoist, mentionable }),
        permissions: permissions.toString(),
      });
      notify('Permissões guardadas.');
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Falhou guardar', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function remove(): Promise<void> {
    const sure = await confirm({
      title: `Apagar o grupo ${role.name}?`,
      body: 'Quem o tinha fica sem ele, e as permissões que vinham daqui desaparecem.',
      confirm: 'Apagar grupo',
      danger: true,
    });
    if (!sure) return;
    try {
      await rest.deleteRole(guild.id, role.id);
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Falhou apagar o grupo', 'error');
    }
  }

  async function toggleMember(userId: string, had: boolean): Promise<void> {
    try {
      await rest.setMemberRoles(guild.id, userId, had ? { remove: [role.id] } : { add: [role.id] });
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Falhou mudar as permissões', 'error');
    }
  }

  return (
    <div className="role-editor">
      <div className="field-row">
        <label className="field">
          Nome
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={LIMITS.roleNameMax}
            disabled={isEveryone}
          />
        </label>
        <label className="field color">
          Cor
          <input
            type="color"
            value={colorHex(color === 0 ? 0x9b9299 : color)}
            onChange={(e) => setColor(parseInt(e.target.value.slice(1), 16))}
            disabled={isEveryone}
          />
        </label>
      </div>

      {!isEveryone && (
        <div className="role-flags">
          <label>
            <input type="checkbox" checked={hoist} onChange={(e) => setHoist(e.target.checked)} />
            Mostrar em separado na lista de membros
          </label>
          <label>
            <input
              type="checkbox"
              checked={mentionable}
              onChange={(e) => setMentionable(e.target.checked)}
            />
            Qualquer um pode mencionar este grupo
          </label>
        </div>
      )}

      <div className="permissions">
        {GROUPS.map((group) => (
          <fieldset key={group.title}>
            <legend>{group.title}</legend>
            {group.items.map((item) => (
              <label key={item.label}>
                <input
                  type="checkbox"
                  checked={(permissions & item.bit) === item.bit}
                  onChange={(e) =>
                    setPermissions((p) => (e.target.checked ? p | item.bit : p & ~item.bit))
                  }
                />
                {item.label}
              </label>
            ))}
          </fieldset>
        ))}
      </div>

      {!isEveryone && (
        <div className="role-members">
          <h3>Quem tem estas permissões</h3>
          <ul>
            {members.map((m) => {
              const had = m.roles.includes(role.id);
              return (
                <li key={m.userId}>
                  <label>
                    <input
                      type="checkbox"
                      checked={had}
                      onChange={() => void toggleMember(m.userId, had)}
                    />
                    {m.nick ?? m.user.displayName}
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="role-actions">
        {!isEveryone && (
          <button className="link danger" onClick={() => void remove()}>
            Apagar grupo
          </button>
        )}
        <div className="spacer" />
        <button className="primary" disabled={!dirty || busy} onClick={() => void save()}>
          Guardar
        </button>
      </div>
    </div>
  );
}
